import { ForwardedRef, forwardRef, ReactNode, useCallback } from 'react'

import AppCheckboxBadged from './AppCheckboxBadged'
import { CheckboxGroupOption } from './AppCheckboxGroup.types'

type CheckboxGroupFC = {
  <T extends string | number>(
    props: Props<T> & { ref?: ForwardedRef<HTMLInputElement> }
  ): ReactNode
  displayName?: string
}

type Props<T> = {
  options: CheckboxGroupOption<T>[] | CheckboxGroupOption<T>[][]
  title?: string
  className?: string
  value: T[]
  onChange: (updatedValue: T[], selectedOption: CheckboxGroupOption<T>) => void
  closable?: boolean
}

const AppCheckboxBadgedGroup = forwardRef(
  <T extends string | number>(
    {
      options,
      title,
      className,
      value,
      onChange,
      closable,
      ...props
    }: Props<T>,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const handleChange = useCallback(
      (selectedOption: CheckboxGroupOption<T>) => {
        const updatedValue = selectedOption.checked
          ? [...value, selectedOption.value]
          : value.filter(item => item !== selectedOption.value)

        onChange(updatedValue, selectedOption)
      },
      [onChange, value]
    )

    const renderCheckboxBadged = useCallback(
      (option: CheckboxGroupOption<T>) => (
        <AppCheckboxBadged
          ref={ref}
          closable={closable}
          key={option.value}
          checked={value.includes(option.value)}
          onChange={({ target }) =>
            handleChange({ ...option, checked: target.checked })
          }
          {...option}
          {...props}
        />
      ),
      [closable, handleChange, props, ref, value]
    )

    return (
      <div className={className}>
        {title && (
          <div className="mb-8 font-sans text-black/50 text-16">{title}</div>
        )}
        <div className="flex flex-wrap gap-8">
          {options.map(option =>
            Array.isArray(option) ? (
              <div
                key={JSON.stringify(option)}
                className="flex w-full flex-wrap gap-8"
              >
                {option.map(item => renderCheckboxBadged(item))}
              </div>
            ) : (
              renderCheckboxBadged(option)
            )
          )}
        </div>
      </div>
    )
  }
) as CheckboxGroupFC

AppCheckboxBadgedGroup.displayName = 'AppCheckboxBadgedGroup'
export default AppCheckboxBadgedGroup
